/**
 * RequireAdminAuth — admin shell entry guard (STORY-008-006).
 *
 * Admits a user holding ANY admin-prefixed permission (isAdminPermission over
 * users./marketplace./content./ops./ooc./feedback.). Server RBAC remains the
 * real enforcement; this is UX only.
 *
 * SCRUM-145 — `/admin` belongs to the ADMIN door. Everyone who can't pass is
 * handed to `/admin/login`, never to a consumer surface:
 *
 *   • unauthenticated  → `/admin/login?return_to=<path>`. The consumer `/login`
 *     is the wrong front door for the admin panel, and after signing in there
 *     the visitor never gets back here.
 *   • authenticated non-admin (e.g. a seller who typed /admin) → the admin door
 *     flagged `?wrong_door=1`, which renders the "no admin access" panel. This
 *     used to dump them on `/` — the public marketing landing, complete with a
 *     "Start your renovation" signup CTA, with no hint of what happened.
 *     Deliberately NO `return_to`: `RedirectIfAuthenticated` honours a safe
 *     return_to BEFORE resolving identity, so carrying one bounces straight
 *     back through this guard and loops.
 *
 * The `/me` read is gated on `isAuthenticated`. Firing it without a token 401s,
 * and the api-client's persistent-401 handler hard-navigates to whatever door
 * it computes — beating this component's own <Navigate/> and double-wrapping
 * `return_to` into `/login?return_to=%2Flogin%3Freturn_to%3D…`.
 */
import { type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import { registerAdminI18n, ADMIN_NS } from "@/features/admin/api";
import { isAdminPermission } from "@/features/admin/navConfig";
import { loginDoorForPath } from "@/features/auth/destination";
import { useMe } from "@/features/settings/api";
import {
  selectAuthBootstrapped,
  selectIsAuthenticated,
  useAuthStore,
} from "@/lib/auth-store";

function GuardSpinner(): ReactElement {
  const { t } = useTranslation(ADMIN_NS);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t("loading")}
      data-testid="admin-guard-loading"
      className="flex min-h-screen w-full items-center justify-center"
    >
      <span
        aria-hidden="true"
        className="size-8 animate-spin rounded-full border-2 border-[var(--g-hairline)] border-t-[var(--g-primary)]"
      />
    </div>
  );
}

export function RequireAdminAuth({ children }: { children: ReactNode }): ReactElement {
  registerAdminI18n();

  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const bootstrapped = useAuthStore(selectAuthBootstrapped);
  const location = useLocation();
  const { data: profile, isLoading: meLoading } = useMe({ enabled: isAuthenticated });

  // Boot-time silent refresh still in flight (no token yet) → spinner, not bounce.
  if (!isAuthenticated && !bootstrapped) {
    return <GuardSpinner />;
  }
  if (!isAuthenticated) {
    const door = loginDoorForPath(location.pathname);
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${door}?return_to=${returnTo}`} replace />;
  }
  if (meLoading || profile === undefined) {
    return <GuardSpinner />;
  }
  const permissions: string[] = profile.permissions ?? [];
  if (!permissions.some(isAdminPermission)) {
    // The door itself explains this (`?wrong_door=1` → the "no admin access"
    // panel), so no toast: `toast()` here would be a side effect fired during
    // render, and the redirect unmounts the tree before it can be read anyway.
    return <Navigate to={`${loginDoorForPath(location.pathname)}?wrong_door=1`} replace />;
  }
  return <>{children}</>;
}

export default RequireAdminAuth;
